import Controller from '@ember/controller';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action, get, set } from '@ember/object';
import config from 'garaje/config/environment';
import { buildUrl, mapTimelineEvent } from 'garaje/utils/integration-health';

const TIME_RANGE_MS = {
  'Last 1 hour': 60 * 60 * 1000,
  'Last 24 hours': 24 * 60 * 60 * 1000,
  'Last 7 days': 7 * 24 * 60 * 60 * 1000,
  'Last 30 days': 30 * 24 * 60 * 60 * 1000,
};

// Bucket size for each time range (matches server-side chart bucket granularity).
// Used by liveUpdate() to fetch only the most recent bucket of timeline events.
const BUCKET_SIZE_MS = {
  'Last 1 hour': 5 * 60 * 1000, // 5-minute buckets
  'Last 24 hours': 60 * 60 * 1000, // 1-hour buckets
  'Last 7 days': 8 * 60 * 60 * 1000, // 8-hour buckets
  'Last 30 days': 24 * 60 * 60 * 1000, // 1-day buckets
};

export default class IntegrationHealthDashboardController extends Controller {
  queryParams = ['preview'];

  @tracked preview = false;
  @tracked showLocationFilter = false;
  @tracked selectedTimeRange = 'Last 1 hour';
  @tracked selectedInstalls = []; // [] = All installs
  @tracked selectedEventTypes = []; // [] = All event types
  @tracked selectedLocations = []; // [] = All locations
  @tracked selectedIntegrationTypes = []; // [] = All integration types
  @tracked selectedStatuses = []; // [] = All statuses
  @tracked isLoading = false;
  @tracked page = 1;

  @tracked _chartLoadingBucketMs = null; // Set<number> of chunk.start values still pending, null when not progressive
  @tracked _chartProgressLoaded = 0;
  @tracked _chartProgressTotal = 0;
  @tracked chartLoadKey = 0;

  _fetchDataGeneration = 0;

  @service ajaxFetch;
  @service state;
  @service currentAdmin;

  perPage = 20;
  _refreshTimer = null;

  @tracked _healthDataGeneral = null; // GET /api/v2/monitoring/health — no time filter (used for dropdown filters)
  @tracked _timelineData = null; // GET /api/v2/debugging/table + /api/v2/debugging/chart

  timeRangeOptions = ['Last 1 hour', 'Last 24 hours', 'Last 7 days', 'Last 30 days'];

  // String equivalents for the filter drawer (single-select native <select> elements).
  // When multiple values are selected via the top bar, the drawer shows the first one.
  get selectedLocation() {
    return this.selectedLocations[0] ?? 'All locations';
  }
  get selectedInstall() {
    return this.selectedInstalls[0] ?? null;
  }
  get selectedEventType() {
    return this.selectedEventTypes[0] ?? 'All event types';
  }
  get selectedIntegrationType() {
    return this.selectedIntegrationTypes[0] ?? 'All integration types';
  }
  get selectedStatus() {
    return this.selectedStatuses[0] ?? 'All statuses';
  }

  get _mappedEvents() {
    const nameById = Object.fromEntries(this._integrations.map((i) => [String(i.installId), i.integrationName]));
    return (this._timelineData?.events ?? []).map((raw) => {
      const event = mapTimelineEvent(raw);
      event.integrationName = nameById[String(raw.installId)] ?? event.integrationType;
      return event;
    });
  }

  get _meta() {
    return this._timelineData?.meta ?? { total: 0, page: 1, perPage: this.perPage };
  }

  get _integrations() {
    return this._healthDataGeneral?.integrations ?? [];
  }

  get _filteredIntegrations() {
    const myLocationId = this.state.currentLocation?.id;
    return this._integrations.filter((i) => {
      if (this.selectedInstalls.length > 0) return this.selectedInstalls.includes(String(i.installId));
      if (!this.showLocationFilter) return i.location != null && String(i.location.id) === String(myLocationId);
      if (this.selectedLocations.length > 0) return this.selectedLocations.includes(String(i.location?.id));
      return true;
    });
  }

  // Base set of integrations for building dropdown options.
  // In regular mode: scoped to the current location.
  // In global overview mode: scoped to selectedLocations if any, otherwise all.
  get _scopedIntegrations() {
    const myLocationId = this.state.currentLocation?.id;
    if (!this.showLocationFilter) {
      return this._integrations.filter((i) => i.location != null && String(i.location.id) === String(myLocationId));
    }
    if (this.selectedLocations.length > 0) {
      return this._integrations.filter((i) => this.selectedLocations.includes(String(i.location?.id)));
    }
    return this._integrations;
  }

  get installOptions() {
    const installs = this._scopedIntegrations.map((i) => {
      const locLabel = i.location ? i.location.name || String(i.location.id) : null;
      const value = String(i.installId);
      return {
        label: locLabel ? `${i.integrationName} — ${locLabel}` : i.integrationName,
        value,
        integrationName: i.integrationName,
        locationId: i.location?.id ?? null,
        locationName: locLabel,
      };
    });
    return [{ label: 'All integrations', value: '' }, ...installs];
  }

  get locationOptions() {
    const seen = new Set();
    const locs = [];
    for (const i of this._integrations) {
      // Company-scoped integrations have no location — skip them here
      if (!i.location) continue;
      if (!seen.has(String(i.location.id))) {
        seen.add(String(i.location.id));
        locs.push({ label: i.location.name ?? String(i.location.id), value: String(i.location.id) });
      }
    }
    // If any selected location isn't in the health data (e.g. no integrations there yet,
    // or health hasn't loaded), still include it so the dropdown stays functional.
    for (const locId of this.selectedLocations) {
      if (!seen.has(locId)) {
        locs.push({ label: locId, value: locId });
        seen.add(locId);
      }
    }
    return [{ label: 'All locations', value: 'All locations' }, ...locs];
  }

  get eventTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._scopedIntegrations) {
      for (const opType of i.activeOperationTypes ?? []) {
        if (!seen.has(opType)) {
          seen.add(opType);
          const stripped = opType.replace(/^INTEGRATION_OPERATION_TYPE_/, '');
          const label = stripped
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
          types.push({ label, value: opType });
        }
      }
    }
    return [{ label: 'All event types', value: 'All event types' }, ...types];
  }

  // Options without the "All" sentinel — used by PowerSelectMultiple in the top bar.
  get locationOptionsMulti() {
    return this.locationOptions.slice(1);
  }
  get installOptionsMulti() {
    return this.installOptions.slice(1);
  }
  get eventTypeOptionsMulti() {
    return this.eventTypeOptions.slice(1);
  }

  // Selected option arrays for PowerSelectMultiple @selected bindings.
  get selectedLocationOptions() {
    if (this.selectedLocations.length === 0) return [];
    return this.locationOptions.filter((opt) => this.selectedLocations.includes(opt.value));
  }
  get selectedInstallOptions() {
    if (this.selectedInstalls.length === 0) return [];
    return this.installOptions.filter((opt) => this.selectedInstalls.includes(opt.value));
  }
  get selectedEventTypeOptions() {
    if (this.selectedEventTypes.length === 0) return [];
    return this.eventTypeOptions.filter((opt) => this.selectedEventTypes.includes(opt.value));
  }

  // Single-option getters kept for any legacy template references.
  get selectedLocationOption() {
    return this.locationOptions.find((opt) => opt.value === this.selectedLocation) ?? null;
  }
  get selectedInstallOption() {
    const val = this.selectedInstall ?? '';
    return this.installOptions.find((opt) => opt.value === val) ?? null;
  }
  get selectedEventTypeOption() {
    return this.eventTypeOptions.find((opt) => opt.value === this.selectedEventType) ?? null;
  }

  get selectedIntegrationTypeOption() {
    return this.integrationTypeOptions.find((opt) => opt.value === this.selectedIntegrationType) ?? null;
  }

  get selectedStatusOption() {
    return this.statusOptions.find((opt) => opt.value === this.selectedStatus) ?? null;
  }

  get integrationTypeOptions() {
    const seen = new Set();
    const types = [];
    for (const i of this._scopedIntegrations) {
      if (i.integrationType && !seen.has(i.integrationType)) {
        seen.add(i.integrationType);
        const label = i.integrationType
          .replace(/^INTEGRATION_TYPE_/, '')
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase());
        types.push({ label, value: i.integrationType });
      }
    }
    return [{ label: 'All integration types', value: 'All integration types' }, ...types];
  }

  statusOptions = [
    { label: 'All statuses', value: 'All statuses' },
    { label: 'Success', value: 'OPERATION_STATUS_OK' },
    { label: 'Failed', value: 'OPERATION_STATUS_FAILED' },
    { label: 'Partial', value: 'OPERATION_STATUS_PARTIAL' },
  ];

  get integrationTypeOptionsMulti() {
    return this.integrationTypeOptions.slice(1);
  }
  get statusOptionsMulti() {
    return this.statusOptions.slice(1);
  }
  get selectedIntegrationTypeOptions() {
    if (this.selectedIntegrationTypes.length === 0) return [];
    return this.integrationTypeOptions.filter((opt) => this.selectedIntegrationTypes.includes(opt.value));
  }
  get selectedStatusOptions() {
    if (this.selectedStatuses.length === 0) return [];
    return this.statusOptions.filter((opt) => this.selectedStatuses.includes(opt.value));
  }

  get isChartProgressing() {
    return this._chartLoadingBucketMs !== null && this._chartLoadingBucketMs.size > 0;
  }

  get filteredEvents() {
    return this._mappedEvents;
  }

  get recordCount() {
    return this._meta.total;
  }

  get pageCount() {
    return Math.ceil(this.recordCount / this.perPage);
  }

  get paginatedEvents() {
    return this._mappedEvents;
  }

  startAutoRefresh() {
    this.stopAutoRefresh();
    this._refreshTimer = setInterval(() => this.liveUpdate(), 60_000);
  }

  stopAutoRefresh() {
    if (this._refreshTimer) {
      clearInterval(this._refreshTimer);
      this._refreshTimer = null;
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    this.stopAutoRefresh();
  }

  async fetchData() {
    this.isLoading = true;
    this._fetchDataGeneration++;
    this.chartLoadKey++;
    const gen = this._fetchDataGeneration;

    const now = Date.now();
    const rangeMs = TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    const startTime = now - rangeMs;
    const dayMs = 24 * 60 * 60 * 1000;

    const companyId = String(this.state.currentCompany?.id);
    const isGlobal = this.showLocationFilter;
    const locationId = this.state.currentLocation?.id;

    const activeFilters = {
      ...(this.selectedInstalls.length > 0 && { installId: this.selectedInstalls }),
      ...(this.selectedEventTypes.length > 0 && { eventType: this.selectedEventTypes }),
      ...(this.selectedLocations.length > 0 && { location: this.selectedLocations }),
      ...(this.selectedStatuses.length > 0 && { outcome: this.selectedStatuses }),
      ...(this.selectedIntegrationTypes.length > 0 && { integrationType: this.selectedIntegrationTypes }),
      ...(this.preview && { preview: this.preview }),
    };

    const healthBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/monitoring/health`;
    const tableBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/table`;
    const chartBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/chart`;

    try {
      if (rangeMs > dayMs) {
        // Phase 1 — health + table: fast, unblocks the page immediately.
        const [healthDataGeneral, tableData] = await Promise.all([
          this.ajaxFetch.request(
            buildUrl(healthBase, {
              companyId,
              rawIntegrationOperations: 'true',
              ...(!isGlobal && locationId && { locationId: String(locationId) }),
              ...(this.preview && { preview: this.preview }),
            }),
          ),
          this.ajaxFetch.request(
            buildUrl(tableBase, {
              companyId,
              startTime: String(startTime),
              endTime: String(now),
              page: String(this.page),
              perPage: String(this.perPage),
              rawIntegrationOperations: 'true',
              ...activeFilters,
            }),
          ),
        ]);
        if (gen !== this._fetchDataGeneration) return;
        this._healthDataGeneral = healthDataGeneral;
        this._timelineData = {
          events: tableData?.events ?? [],
          meta: tableData?.meta ?? { total: 0, page: this.page, perPage: this.perPage },
          chartData: [],
          summary: null,
        };
        this.isLoading = false;

        // Phase 2 — chart: loads in 1-day chunks, animating as each arrives.
        const bucketSizeMs = BUCKET_SIZE_MS[get(this, 'selectedTimeRange')];
        await this._fetchChartProgressive(gen, companyId, startTime, now, activeFilters, chartBase, bucketSizeMs);
      } else {
        // Single call for ≤ 1 day — no progressive load needed.
        const [healthDataGeneral, tableData, chartData] = await Promise.all([
          this.ajaxFetch.request(
            buildUrl(healthBase, {
              companyId,
              rawIntegrationOperations: 'true',
              ...(!isGlobal && locationId && { locationId: String(locationId) }),
              ...(this.preview && { preview: this.preview }),
            }),
          ),
          this.ajaxFetch.request(
            buildUrl(tableBase, {
              companyId,
              startTime: String(startTime),
              endTime: String(now),
              page: String(this.page),
              perPage: String(this.perPage),
              rawIntegrationOperations: 'true',
              ...activeFilters,
            }),
          ),
          this.ajaxFetch.request(
            buildUrl(chartBase, {
              companyId,
              startTime: String(startTime),
              endTime: String(now),
              rawIntegrationOperations: 'true',
              ...activeFilters,
            }),
          ),
        ]);
        if (gen !== this._fetchDataGeneration) return;
        this._healthDataGeneral = healthDataGeneral;
        this._timelineData = {
          events: tableData?.events ?? [],
          meta: tableData?.meta ?? { total: 0, page: this.page, perPage: this.perPage },
          chartData: chartData?.chartData ?? [],
          summary: chartData?.summary ?? null,
        };
      }
    } finally {
      this.isLoading = false;
      if (gen === this._fetchDataGeneration) {
        this._chartLoadingBucketMs = null;
      }
    }
  }

  async _fetchChartProgressive(gen, companyId, startTime, now, activeFilters, chartBase, bucketSizeMs) {
    const dayMs = 24 * 60 * 60 * 1000;

    // Build 1-day chunks, most-recent-first so the chart fills in from the right.
    const chunks = [];
    let end = now;
    while (end > startTime) {
      const start = Math.max(startTime, end - dayMs);
      chunks.push({ start, end });
      end = start;
    }

    this._chartLoadingBucketMs = new Set(chunks.map((c) => c.start));
    this._chartProgressTotal = chunks.length;
    this._chartProgressLoaded = 0;

    for (const chunk of chunks) {
      if (gen !== this._fetchDataGeneration) return;
      try {
        const chartResponse = await this.ajaxFetch.request(
          buildUrl(chartBase, {
            companyId,
            startTime: String(chunk.start),
            endTime: String(chunk.end),
            rawIntegrationOperations: 'true',
            ...(bucketSizeMs != null && { bucketSizeMs: String(bucketSizeMs) }),
            ...activeFilters,
          }),
        );
        if (gen !== this._fetchDataGeneration) return;

        const newBuckets = chartResponse?.chartData ?? [];
        const merged = [...(this._timelineData?.chartData ?? [])];
        for (const b of newBuckets) {
          const idx = merged.findIndex((x) => x.startMs === b.startMs);
          // First-seen wins: the most-recent-window call sees boundary buckets first
          // and has complete data; older calls clip at their endTime boundary.
          if (idx < 0) merged.push(b);
        }
        merged.sort((a, b) => a.startMs - b.startMs);

        const pending = new Set(this._chartLoadingBucketMs);
        pending.delete(chunk.start);
        this._chartLoadingBucketMs = pending;
        this._chartProgressLoaded++;
        this._timelineData = {
          ...this._timelineData,
          chartData: merged,
          summary: chartResponse?.summary ?? this._timelineData?.summary,
        };
      } catch {
        if (gen !== this._fetchDataGeneration) return;
        const pending = new Set(this._chartLoadingBucketMs);
        pending.delete(chunk.start);
        this._chartLoadingBucketMs = pending;
        this._chartProgressLoaded++;
      }
    }
    this._chartLoadingBucketMs = null;
  }

  // Silent incremental refresh — no loading overlay. Fetches health stats for the full
  // selected range plus timeline events for the most recent bucket only, then merges new
  // events into the existing list without duplicates. Called by the 60-second auto-refresh
  // timer so the page stays live without the pulsing overlay.
  async liveUpdate() {
    const now = Date.now();
    const rangeMs = TIME_RANGE_MS[get(this, 'selectedTimeRange')];
    const bucketMs = BUCKET_SIZE_MS[get(this, 'selectedTimeRange')];
    const bucketStart = now - bucketMs;

    const companyId = String(this.state.currentCompany?.id);

    const activeFilters = {
      ...(this.selectedInstalls.length > 0 && { installId: this.selectedInstalls }),
      ...(this.selectedEventTypes.length > 0 && { eventType: this.selectedEventTypes }),
      ...(this.selectedLocations.length > 0 && { location: this.selectedLocations }),
      ...(this.selectedStatuses.length > 0 && { outcome: this.selectedStatuses }),
      ...(this.selectedIntegrationTypes.length > 0 && { integrationType: this.selectedIntegrationTypes }),
      ...(this.preview && { preview: this.preview }),
    };

    const startTime = now - rangeMs;
    const tableBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/table`;
    const chartBase = `${config.integrationTelemetryApiHost}/a/integration-telemetry/api/v2/debugging/chart`;

    try {
      const [newTimelineData, chartData] = await Promise.all([
        this.ajaxFetch.request(
          buildUrl(tableBase, {
            companyId,
            startTime: String(bucketStart),
            endTime: String(now),
            page: '1',
            perPage: String(this.perPage),
            sinceTime: String(bucketStart),
            rawIntegrationOperations: 'true',
            ...activeFilters,
          }),
        ),
        this.ajaxFetch.request(
          buildUrl(chartBase, {
            companyId,
            startTime: String(startTime),
            endTime: String(now),
            rawIntegrationOperations: 'true',
            ...activeFilters,
          }),
        ),
      ]);

      // Merge new events into the existing list, prepending unseen ones.
      const incoming = newTimelineData?.events ?? [];
      const existingIds = new Set((this._timelineData?.events ?? []).map((e) => e.eventId));
      const fresh = incoming.filter((e) => !existingIds.has(e.eventId));

      // Slide the chart forward using the full chart response for the selected range.
      // For each returned bucket: update it in-place if we already have that startMs,
      // otherwise append it and drop the oldest bucket to keep the array length stable.
      const newChartBuckets = chartData?.chartData ?? [];
      const existingChart = this._timelineData?.chartData ?? [];
      let updatedChart = existingChart;
      if (newChartBuckets.length > 0 && existingChart.length > 0) {
        updatedChart = [...existingChart];
        for (const bucket of newChartBuckets) {
          const idx = updatedChart.findIndex((b) => b.startMs === bucket.startMs);
          if (idx >= 0) {
            updatedChart[idx] = bucket; // refresh in-place (same slot, more events arrived)
          } else {
            updatedChart = [...updatedChart.slice(1), bucket]; // slide window forward
          }
        }
      }

      if (fresh.length > 0 || updatedChart !== existingChart) {
        const mergedEvents =
          fresh.length > 0
            ? [...fresh, ...(this._timelineData?.events ?? [])].slice(0, this.perPage)
            : this._timelineData?.events;

        // Increment summary counts from fresh events
        const existingSummary = this._timelineData?.summary;
        let updatedSummary = existingSummary;
        if (existingSummary && fresh.length > 0) {
          let addSent = 0;
          let addReceived = 0;
          let addAccess = 0;
          for (const e of fresh) {
            const scope = e.operationScope ?? e.operation_scope;
            const dir = e.direction;
            const opType = e.operation?.type ?? e.operationType ?? e.operation_type;
            if (scope === 'OPERATION_SCOPE_VENDOR_API_CALL' && dir === 'OPERATION_DIRECTION_ENVOY_TO_VENDOR') {
              addSent++;
            }
            if (opType === 'INTEGRATION_OPERATION_TYPE_WEBHOOK' || dir === 'OPERATION_DIRECTION_VENDOR_TO_ENVOY') {
              addReceived++;
            }
            // Access events: sum primaryMetricValue from POLL(events_fetched) + WEBHOOK(events_received)
            const metric = e.primaryMetric; // "events_fetched: 42" or "events_received: 5"
            if (metric) {
              const isPoll = opType === 'INTEGRATION_OPERATION_TYPE_POLL' && metric.startsWith('events_fetched');
              const isWebhook = opType === 'INTEGRATION_OPERATION_TYPE_WEBHOOK' && metric.startsWith('events_received');
              if (isPoll || isWebhook) {
                const val = parseInt(metric.split(':')[1]?.trim(), 10);
                if (!isNaN(val)) addAccess += val;
              }
            }
          }
          updatedSummary = {
            ...existingSummary,
            operationsSent: existingSummary.operationsSent + addSent,
            operationsReceived: existingSummary.operationsReceived + addReceived,
            accessEventsReceived: existingSummary.accessEventsReceived + addAccess,
            lastSeenAt: Math.max(existingSummary.lastSeenAt ?? 0, ...fresh.map((e) => e.envoyProcessedAt ?? 0)),
          };
        }

        this._timelineData = {
          ...this._timelineData,
          chartData: updatedChart,
          events: mergedEvents,
          summary: updatedSummary,
        };
      }
    } catch {
      // silently fail — same behaviour as fetchData
    }
  }

  @action setTimeRange(value) {
    set(this, 'selectedTimeRange', value);
    this.page = 1;
    this.fetchData();
  }

  // Top-bar multi-select actions (receive array of option objects from PowerSelectMultiple).
  @action setLocations(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedLocations = values;
    this.selectedInstalls = [];
    this.page = 1;
    this.fetchData();
  }

  @action setInstalls(opts) {
    const values = opts.map((o) => o?.value ?? o).filter(Boolean);
    this.selectedInstalls = values;
    // Mirror single-install auto-selection: when exactly one install is chosen,
    // auto-select its location and integration type (same as the old setInstall action).
    if (values.length === 1) {
      const installData = this._integrations.find((i) => String(i.installId) === values[0]);
      this.selectedLocations = installData?.location ? [String(installData.location.id)] : [];
      this.selectedIntegrationTypes = installData?.integrationType ? [installData.integrationType] : [];
    } else {
      this.selectedLocations = [];
      this.selectedIntegrationTypes = [];
    }
    this.page = 1;
    this.fetchData();
  }

  @action setEventTypes(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedEventTypes = values;
    this.page = 1;
    this.fetchData();
  }

  @action setPage(value) {
    this.page = value;
    if (value === 1) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
    this.fetchData();
  }

  @action setIntegrationTypes(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedIntegrationTypes = values;
    this.page = 1;
    this.fetchData();
  }

  @action setStatuses(opts) {
    const values = opts.map((o) => o?.value ?? o);
    this.selectedStatuses = values;
    this.page = 1;
    this.fetchData();
  }

  get eventLogUrl() {
    return this.showLocationFilter ? '/location-overview/manage/event-log' : '/manage/event-log';
  }

  get showReasonColumn() {
    return (
      this.selectedStatuses.length > 0 &&
      !this.selectedStatuses.includes('OPERATION_STATUS_OK') &&
      (this.selectedStatuses.includes('OPERATION_STATUS_FAILED') ||
        this.selectedStatuses.includes('OPERATION_STATUS_PARTIAL'))
    );
  }

  get showPrimaryMetricColumn() {
    return !this.showReasonColumn;
  }

  get hasFailures() {
    if (!this._timelineData) return false;
    const points = this._timelineData.chartData ?? [];
    return points.some((p) => !p.isEmpty && p.successRate != null && p.successRate < 100);
  }

  @action filterToFailures() {
    this.selectedStatuses = ['OPERATION_STATUS_FAILED', 'OPERATION_STATUS_PARTIAL'];
    this.page = 1;
    this.fetchData();
    setTimeout(() => {
      document.getElementById('events-table')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }
}
